/**
 * @file: MediaStrip.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Compact thumbnail row for `FeedMediaSnapshot[]` payloads. Used in:
 *   - inbox `ThreadRow`: tiny strip alongside the preview line
 *   - thread `MessageCard`: larger tiles below the body
 *
 * Renders up to `max` thumbnails; if the source has more, a small "+N"
 * overlay sits on the last visible tile. Videos (kind starts with
 * `video`) prefer `coverUrl` for the still + show a play badge.
 */

import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { Typography } from '@euler/app/components/typography';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FeedMediaSnapshot } from '@euler/model/feed';
import { FC, memo, useMemo } from 'react';
import { TouchableOpacity, View } from 'react-native';

const isVideoMedia = (m: FeedMediaSnapshot): boolean =>
  (m.kind ?? '').startsWith('video');

type Props = {
  medias: FeedMediaSnapshot[];
  /** Side length of each thumbnail in rpx. Defaults to a "small" 28. */
  size?: number;
  /** Cap on visible thumbnails — defaults to 3. */
  max?: number;
  /** Pixel gap between tiles. Defaults to 4 (rpx). */
  gap?: number;
  /**
   * Tap an image tile to open the full-screen viewer (swipeable across
   * all images in the strip, incl. the hidden +N). Off by default so the
   * tiny inbox-row strip lets the row tap through to the thread; the
   * in-thread bodies opt in.
   */
  previewable?: boolean;
};

export const MediaStrip: FC<Props> = memo(
  ({ medias, size = 28, max = 3, gap = 4, previewable = false }) => {
    const { rpx } = useResponsiveContext();
    const previewImage = usePreviewImage();
    // Full-res image urls (videos excluded) — the viewer's swipe set, so a
    // tap on any image lands on its position and can page to the hidden ones.
    const imageUrls = useMemo(
      () => (medias ?? []).filter(m => !isVideoMedia(m)).map(m => m.url),
      [medias],
    );
    if (!medias || medias.length === 0) return null;
    const visible = medias.slice(0, max);
    const overflow = medias.length - visible.length;
    const tileSize = rpx(size);
    const tileGap = rpx(gap);
    return (
      <View
        css={`
          flex-direction: row;
          align-items: center;
        `}
      >
        {visible.map((m, idx) => {
          const isLast = idx === visible.length - 1;
          const isVideo = isVideoMedia(m);
          const uri = isVideo ? m.coverUrl ?? m.url : m.url;
          // Position of this image within the (video-excluded) swipe set —
          // `visible[idx]` is `medias[idx]`, so count the images before it.
          const imageIndex = isVideo
            ? -1
            : medias.slice(0, idx).filter(x => !isVideoMedia(x)).length;
          const interactive = previewable && !isVideo && !!uri;
          return (
            <TouchableOpacity
              key={`${m.url}:${idx}`}
              activeOpacity={0.85}
              disabled={!interactive}
              onPress={
                interactive
                  ? () => previewImage({ imageUrls, index: imageIndex })
                  : undefined
              }
              css={`
                width: ${tileSize}px;
                height: ${tileSize}px;
                border-radius: ${rpx(4)}px;
                margin-right: ${isLast ? 0 : tileGap}px;
                background-color: #f3f4f6;
                overflow: hidden;
              `}
            >
              <Img
                uri={uri}
                resizeMode="cover"
                css={`
                  width: ${tileSize}px;
                  height: ${tileSize}px;
                `}
              />
              {isVideo && (
                <View
                  css={`
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  <Icon
                    name="play-circle"
                    size={Math.floor(tileSize * 0.45)}
                    color="rgba(255,255,255,0.9)"
                  />
                </View>
              )}
              {isLast && overflow > 0 && (
                <View
                  css={`
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background-color: rgba(0, 0, 0, 0.4);
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  <Typography.Text
                    color="#fff"
                    size={Math.floor(tileSize * 0.32)}
                    style={{ fontFamily: FontFamily.NotoSans.Bold }}
                  >
                    +{overflow}
                  </Typography.Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>
    );
  },
);
